import { createHook } from 'reakit-system';
import { variant } from 'styled-system';
import { SystemStyleObject } from '@styled-system/css';
import { pxToRem } from '@mos-cat/ds-theme';
import { BoxOptions, BoxHTMLProps, useBox } from '../../Box/Box';
import { StylesOptions, useStyles } from '../useStyles';
import { focus } from '../../utils/focusable';
import { WHCM, WHCMstyles } from '../../constants';

export const iconStyles = variant({
  prop: 'kind',
  variants: {
    base: {
      cursor: 'pointer',
      '& .hint': {
        color: 'hintColor',

        [WHCM]: {
          color: 'InfoText',
        },
      },

      '& .icon': {
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'checkableBorderColor',
        backgroundColor: 'checkableBgColor',
        size: 'checkableSize',
        position: 'relative',
        zIndex: 0,
      },
      '& input:focus ~ .icon': focus,

      '& input:checked ~ .icon': {
        borderColor: 'checkableCheckedColor',
        backgroundColor: 'checkableCheckedColor',
      },

      '&:hover  .icon': {
        borderColor: 'checkableHoverBorderColor',
        backgroundColor: 'checkableHoverBgColor',
      },

      '&:hover input:checked ~ .icon': {
        borderColor: 'checkableCheckedHoverColor',
        backgroundColor: 'checkableCheckedHoverColor',
      },
      '&:active .icon': {
        borderColor: 'checkableHoverBorderColor',
        backgroundColor: 'checkableActiveBgColor',
      },
      '&:active input:checked ~ .icon': {
        borderColor: 'checkableCheckedActiveColor',
        backgroundColor: 'checkableCheckedActiveColor',
      },

      [WHCM]: {
        '& .icon, &:hover .icon, &:active .icon': {
          ...WHCMstyles,
          borderColor: 'WindowText',
          backgroundColor: 'Window',
        },
        '& input:checked ~ .icon, &:hover input:checked ~ .icon, &:active input:checked ~ .icon': {
          borderColor: 'WindowText',
          backgroundColor: 'Window',
        },
      },
    },
    error: {
      cursor: 'pointer',
      '& .hint': {
        color: 'hintColor',

        [WHCM]: {
          color: 'InfoText',
        },
      },

      '& .icon': {
        borderWidth: 1,
        borderColor: 'checkableErrorColor',
        backgroundColor: 'checkableBgColor',
        size: 'checkableSize',
        position: 'relative',
        zIndex: 0,
      },
      '& input:focus ~ .icon': focus,

      '& input:checked ~ .icon': {
        borderWidth: 1,
        borderColor: 'checkableErrorColor',
        backgroundColor: 'checkableErrorColor',
      },

      '&:hover .icon': {
        borderColor: 'checkableErrorHoverColor',
        backgroundColor: 'checkableHoverBgColor',
      },

      '&:hover input:checked ~ .icon': {
        borderColor: 'checkableErrorHoverColor',
        backgroundColor: 'checkableErrorHoverColor',
      },

      '&:active .icon': {
        borderColor: 'checkableErrorActiveColor',
        backgroundColor: 'checkableActiveBgColor',
      },

      '&:active input:checked ~ .icon': {
        borderColor: 'checkableErrorActiveColor',
        backgroundColor: 'checkableErrorActiveColor',
      },

      [WHCM]: {
        '& .icon, &:hover .icon, &:active .icon': {
          borderColor: 'Highlight',
          backgroundColor: 'Window',
        },
        '& input:checked ~ .icon, &:hover input:checked ~ .icon, &:active input:checked ~ .icon': {
          borderColor: 'Highlight',
          backgroundColor: 'Window',
        },

        '& input:checked ~ .icon::after': {
          borderColor: 'Highlight !important',
        },
      },
    },
    disabled: {
      cursor: 'default',

      color: 'checkableDisabledLabelColor',

      '& input:disabled ~ .icon, &:hover input:disabled ~ .icon, &:active input:disabled ~ .icon': {
        backgroundColor: 'checkableDisabledBgColor',
        borderColor: 'checkableDisabledBorderColor',
      },
      '& input:checked:disabled ~ .icon, &:hover input:checked:disabled ~ .icon, &:hover input:checked:disabled ~ .icon': {
        backgroundColor: 'checkableDisabledCheckedBgColor',
      },
      '& .hint': {
        color: 'checkableDisabledLabelColor',
      },

      [WHCM]: {
        color: 'GrayText',

        '& input:disabled ~ .icon, &:hover input:disabled ~ .icon, &:active input:disabled ~ .icon': {
          backgroundColor: 'GrayText',
          borderColor: 'GrayText',
        },
        '& input:checked:disabled ~ .icon, &:hover input:checked:disabled ~ .icon, &:active input:checked:disabled ~ .icon': {
          backgroundColor: 'GrayText',
          borderColor: 'GrayText',
          color: 'Window',
        },

        '& input:checked:disabled ~ .icon::after': {
          borderColor: 'white',
        },

        '& .hint': {
          ...WHCMstyles,
          color: 'GrayText',
        },
      },
    },
  },
});

const checkedStyles = {
  Radiobutton: {
    '& input:checked ~ .icon::after': {
      content: "''",
      width: 'radioTickSize',
      maxWidth: 'radioTickSize',
      height: 'radioTickSize',
      maxHeight: 'radioTickSize',
      borderColor: 'checkableTickColor',
      borderRadius: 'round',
      borderWidth: 'radioTickBorderWidth',
      zIndex: 1,
      display: 'block',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',

      [WHCM]: {
        borderColor: 'WindowText',
      },
    },
  },
  Checkbox: {
    '& input:checked ~ .icon::after': {
      content: "''",
      width: 'checkboxTickWidth',
      height: 'checkboxTickHeight',
      borderColor: 'checkableTickColor',
      borderWidth: 'checkboxTickBorderWidth',
      zIndex: 1,
      display: 'block',
      position: 'absolute',
      left: 'checkboxTickLeft',
      top: 'checkboxTickTop',
      transform: 'rotate(-47deg)',

      [WHCM]: {
        borderColor: 'WindowText',
      },
    },
    '& input:indeterminate ~ .icon': {
      backgroundColor: 'checkableCheckedColor',
      borderColor: 'checkableCheckedColor',

      [WHCM]: {
        backgroundColor: 'Window',
        borderColor: 'WindowText',
      },
    },
    '&:hover input:indeterminate ~ .icon': {
      backgroundColor: 'checkableCheckedHoverColor',
      borderColor: 'checkableCheckedHoverColor',

      [WHCM]: {
        backgroundColor: 'Window',
        borderColor: 'WindowText',
      },
    },
    '&:active input:indeterminate ~ .icon': {
      backgroundColor: 'checkableCheckedActiveColor',
      borderColor: 'checkableCheckedActiveColor',
    },
    '& input:indeterminate ~ .icon::after': {
      content: "''",
      backgroundColor: 'white',
      width: pxToRem(10),
      height: 0,
      borderWidth: pxToRem(1.125),
      borderRadius: '5%',
      zIndex: 1,
      display: 'block',
      position: 'absolute',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',

      [WHCM]: {
        borderColor: 'WindowText',
      },
    },
  },
};

export type CheckerIconOptions = {
  label: 'Radiobutton' | 'Checkbox';
  kind: 'base' | 'error' | 'disabled';
  disabled?: boolean;
} & StylesOptions &
  BoxOptions;

export type CheckerIconHtmlProps = Omit<BoxHTMLProps, keyof CheckerIconOptions>;

export const useCheckerIcon = createHook<CheckerIconOptions, BoxHTMLProps>({
  keys: ['label', 'kind'],
  compose: [useStyles, useBox],
  useOptions(options): CheckerIconOptions {
    return {
      ...options,
      styles: {
        ...iconStyles(options),
        ...checkedStyles[options.label],
        ...options.styles,
      } as SystemStyleObject,
    };
  },
});
